import React, { useEffect, useState } from "react";
import { X, Wrench, AlertCircle, Trash2 } from "lucide-react";
import axios from "axios";
import ChatPanel from "./ChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sre-agent`;

const makeSessionId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `sre-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const SREAgentModal = ({ isOpen, onClose }) => {
  const [health, setHealth] = useState(null);
  const [sessionId, setSessionId] = useState(makeSessionId);

  // Regenerate session each time the modal is opened (fresh context per open)
  useEffect(() => {
    if (isOpen) setSessionId(makeSessionId());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get(`${API}/health`)
      .then(({ data }) => setHealth(data))
      .catch(() => setHealth({ available: false }));
  }, [isOpen]);

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/clear/${sessionId}`);
    } catch (e) {
      // Best-effort — even if server clear fails, regenerate the session locally
      // so the user gets a fresh chat experience.
    }
    setSessionId(makeSessionId());
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="sre-modal"
    >
      <div
        className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[900px]
                   flex flex-col border border-slate-600 overflow-hidden"
      >
        {/* Header — flex-shrink-0 so it's ALWAYS visible */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-600 bg-slate-700/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">SRE Agent · Phase 1</h2>
              <p className="text-xs text-slate-300">
                {health
                  ? health.available
                    ? `model: ${health.model}`
                    : "service unavailable"
                  : "loading…"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={clearChat}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors"
              title="Clear chat"
              data-testid="sre-modal-clear"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              data-testid="sre-modal-close"
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body — flex-1 min-h-0 so ChatPanel can fill remaining space WITHOUT pushing header out */}
        {health && !health.available ? (
          <div className="flex-1 min-h-0 flex items-center justify-center p-6">
            <div className="text-center max-w-md">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-white font-medium">SRE Agent is not available</p>
              <p className="text-sm text-slate-300 mt-2">
                Either the feature flag (<code>SRE_AGENT_ENABLED</code>) is off,
                or the LLM key is not configured on the backend.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* key={sessionId} → remount ChatPanel on clear so internal messages reset cleanly */}
            <ChatPanel key={sessionId} sessionId={sessionId} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SREAgentModal;
